import React, { useMemo } from 'react';
import { Table, Space, Popconfirm, Button } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { KeyOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

import { Account, AccountLevel } from '../../../Modules/Account/Model/Account';
import { canManage, canResetPassword as canResetPasswordOf, levelKor } from '../../../Utils/permission';
import dayjs from 'dayjs';

interface Props {
  currentUser: { id: number; grade: AccountLevel };
  data: Account[];
  total: number;
  perPage: number;
  currentPage: number;
  onPageChange: (p: number) => void;
  onEdit:   (a: Account) => void;
  onDelete: (id: number) => void;
  onResetPassword: (account: Account) => void;
}

const AccountTable: React.FC<Props> = ({
  currentUser, data, total, perPage, currentPage,
  onPageChange, onEdit, onDelete, onResetPassword,
}) => {
  /* ---------------- columns ---------------- */
  const columns: ColumnsType<Account> = useMemo(() => [
    {
      title: '순번',
      width: 60,
      render: (_1, _2, i) => (currentPage - 1) * perPage + i + 1,
    },
    { title: '회원등급', dataIndex: 'grade', render: (g: AccountLevel) => levelKor[g] },
    { title: '아이디',    dataIndex: 'username' },
    { title: '회원명',    dataIndex: 'name' },
    { title: '메모',     dataIndex: 'memo' },
    { title: '가입일',    dataIndex: 'created_at', render: (v: string) =>
            dayjs(v).isValid()
              ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
              : '-',
        },
    {
      title: '관리',
      width: 240,
      render: (_, rec) => {
        const canEdit   = canManage(currentUser, rec) && rec.grade !== 'ADMIN';
        const canDelete = currentUser.grade === 'ADMIN'
                          && rec.id !== currentUser.id
                          && rec.grade !== 'ADMIN';
        const canResetPassword = canResetPasswordOf(currentUser, rec);

        if (!canEdit && !canDelete && !canResetPassword) return null;

        return (
          <Space size="small">
            {canEdit && (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(rec)}
              >
                수정
              </Button>
            )}
            {canResetPassword && (
              <Button
                size="small"
                icon={<KeyOutlined />}
                type="default"
                style={{ 
                  borderColor: '#1890ff',
                  color: '#1890ff'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onResetPassword(rec);
                }}
              >
                비밀번호 변경
              </Button>
            )}
            {canDelete && (
              <Popconfirm
                title="삭제하시겠습니까?"
                okText="예"
                cancelText="아니오"
                onConfirm={e => { e?.stopPropagation(); onDelete(rec.id); }}
                onPopupClick={e => e.stopPropagation()}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={e => e.stopPropagation()}
                >
                  삭제
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ], [currentUser, currentPage, perPage, onEdit, onDelete, onResetPassword]);

  /* -------------- render table -------------- */
  return (
    <Table
      size="small"
      rowKey="id"
      dataSource={data}
      columns={columns}
      pagination={{
        current: currentPage,
        pageSize: perPage,
        total,
        onChange: onPageChange,
        showSizeChanger: false,
      }}
      locale={{ emptyText: '조회된 데이터가 없습니다.' }}
       scroll={{ x: 'max-content' }}
    />
  );
};

export default AccountTable;
